import { useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { PiMicrophone, PiMicrophoneSlash } from 'react-icons/pi'
import to from 'await-to-js'
import useApiService from 'services/api'
import FbButton from 'components/FbUI/FbButton'
import { Alert } from 'components/FbUI/Alert'
import { MobileModal } from 'components/UI/Modal/MobileModal'
import { useMountEffect } from 'hooks/useMountEffect'
import { useStreamingTranscription } from 'hooks/useStreamingTranscription'
import { ContactCardPrefill, TranscriptAnalysis } from 'models/contacts'

const ANALYZE_DEBOUNCE_MS = 3000

export default function MobileSpeakContact() {
  const navigate = useNavigate()
  const api = useApiService()
  const [analysis, setAnalysis] = useState<TranscriptAnalysis | undefined>()

  const timerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)
  const analyzedRef = useRef('') // last transcript actually analyzed

  const runAnalyze = async (transcript: string) => {
    if (!transcript.trim() || transcript === analyzedRef.current) return
    analyzedRef.current = transcript
    const [, result] = await to(api.analyzeTranscript(transcript))
    if (result) setAnalysis(result)
  }

  // Debounced live analysis. Scheduling happens in the turn-finalized handler
  // (fired from the WebSocket message handler inside useStreamingTranscription),
  // never in the render body — so it runs exactly once per finalized turn and is
  // safe under Strict Mode's double-invoke. Each new turn resets the debounce.
  const {
    isRecording,
    finalTranscript,
    partialTranscript,
    error,
    start,
    stop,
  } = useStreamingTranscription({
    onTurnFinalized: (transcript) => {
      if (timerRef.current) clearTimeout(timerRef.current)
      timerRef.current = setTimeout(
        () => void runAnalyze(transcript),
        ANALYZE_DEBOUNCE_MS
      )
    },
  })

  useMountEffect(() => () => {
    if (timerRef.current) clearTimeout(timerRef.current)
  })

  const handleDone = async () => {
    stop()
    if (timerRef.current) clearTimeout(timerRef.current)
    // Definitive analysis of the full transcript so the freshest fields prefill.
    let result = analysis
    if (finalTranscript.trim() && finalTranscript !== analyzedRef.current) {
      const [, fresh] = await to(api.analyzeTranscript(finalTranscript))
      if (fresh) result = fresh
    }
    const prefill: ContactCardPrefill | undefined = result
    navigate('/contacts/create/manual', {
      state: prefill
        ? { prefill, companyQuery: prefill.company_name }
        : undefined,
    })
  }

  return (
    <MobileModal
      handleBack={() => {
        stop()
        navigate(-1)
      }}
      header={
        <div className="p-2">
          <div className="text-xs uppercase tracking-wide text-fg-tertiary mb-1">
            Create a contact
          </div>
          <h1 className="text-2xl font-semibold text-fg">Speak it</h1>
          <div className="text-xs text-fg-tertiary mt-2">
            Describe who you met — name, company, how to reach them.
          </div>
        </div>
      }
      footer={
        <div className="flex gap-4 p-2 justify-between">
          <div className="w-1/2">
            <FbButton
              className="w-full"
              type="button"
              size="mdlg"
              variant="secondary"
              onClick={isRecording ? stop : () => void start()}
            >
              {isRecording ? 'Stop' : 'Record'}
            </FbButton>
          </div>
          <div className="w-1/2">
            <FbButton
              className="w-full"
              type="button"
              size="mdlg"
              variant="primary"
              onClick={() => void handleDone()}
            >
              Done
            </FbButton>
          </div>
        </div>
      }
    >
      <div className="flex flex-col gap-4 p-4">
        {error && <Alert color="warning" description={error} />}

        <div className="flex items-center gap-2 text-sm text-fg-secondary">
          {isRecording ? (
            <PiMicrophone className="h-5 w-5 text-brand animate-pulse" />
          ) : (
            <PiMicrophoneSlash className="h-5 w-5 text-fg-tertiary" />
          )}
          <span>{isRecording ? 'Listening…' : 'Tap Record to start'}</span>
        </div>

        <div className="min-h-24 rounded-xl border border-line p-3 text-sm text-fg">
          {finalTranscript}{' '}
          <span className="text-fg-tertiary">{partialTranscript}</span>
          {!finalTranscript && !partialTranscript && (
            <span className="text-fg-tertiary">
              Your words will appear here…
            </span>
          )}
        </div>

        {analysis && analysis.follow_up_questions.length > 0 && (
          <div>
            <div className="text-xs font-medium uppercase tracking-wide text-fg-tertiary mb-2">
              Still needed — ask about:
            </div>
            <div className="flex flex-wrap gap-2">
              {analysis.follow_up_questions.map((q) => (
                <span
                  key={q}
                  className="rounded-full border border-line bg-surface-sunken px-3 py-1 text-xs text-fg-secondary"
                >
                  {q}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>
    </MobileModal>
  )
}
